import { Syringe, ListChecks, ClipboardList, MapPin } from 'lucide-react';
import { tchOr, BB_DOC } from './tchOr';

/*
 * Texas Children's Regional rotation, at West Campus. Built from the Regional Anesthesia
 * Rotation Orientation Manual 2021 on Blackboard. See docs/blackboard-tch.md.
 * Documents extend the General OR rotation; contacts and locations are West Campus.
 */

const MANUAL = BB_DOC('_323656_1');
const wc = (n) => `832227${n.slice(1)}`; // 7xxxx extensions dial as 832-227-xxxx

export const tchRegional = {
  accent: tchOr.accent,
  icon: Syringe,
  tagline: 'West Campus blocks, asleep, under faculty supervision',
  notesKey: 'notes:texas-childrens:regional',

  toolSections: [
    { title: 'Clinical tools', tools: [
      { id: 'checklist', title: 'Regional Safety Checklist', subtitle: 'Briefing, timeout, debriefing', icon: ListChecks, action: 'guide:checklist', featured: true },
      { id: 'duties', title: 'Block Day', subtitle: 'Finding cases, consent, setup, follow-up', icon: ClipboardList, action: 'guide:duties' },
    ] },
    { title: 'Logistics', tools: [
      { id: 'campus', title: 'Getting In', subtitle: 'Parking, the entrance, lockers, attire', icon: MapPin, action: 'guide:campus' },
    ] },
  ],

  contacts: {
    storageKey: 'tch-regional-favorites',
    groups: [
      { id: 'regional', title: 'West Campus regional', open: true, items: [
        { label: 'West Campus GOAT', phone: '8322274794', note: 'Anesthesia board runner' },
        { label: 'Dr Handley', phone: '8327730837', note: 'Voalte' },
        { label: 'West Campus main number', phone: '8322271000' },
      ] },
      { id: 'or', title: 'OR', items: [
        { label: 'OR control desk', phone: wc('72180') },
        { label: 'OR charge', phone: wc('74530') },
        { label: 'Preop and holding', phones: [{ label: '72054', phone: wc('72054') }, { label: '72055', phone: wc('72055') }] },
        { label: 'PACU', phone: wc('71643') },
        { label: 'PACU charge', phone: wc('74550') },
        { label: 'OR 1', phone: wc('72186') },
        { label: 'OR 2', phone: wc('72187') },
        { label: 'OR 3', phone: wc('72196') },
        { label: 'OR 4', phone: wc('72194') },
        { label: 'OR 7', phone: wc('72721') },
        { label: 'OR 8', phone: wc('72717') },
      ] },
      { id: 'support', title: 'Support', items: [
        { label: 'Pharmacy', phones: [{ label: '71850', phone: wc('71850') }, { label: '74865', phone: wc('74865') }] },
        { label: 'Blood bank', phone: wc('71676') },
        { label: 'Respiratory', phone: wc('72080') },
        { label: 'PICU', phone: wc('73400') },
        { label: 'Emergency Center', phone: wc('71300') },
        { label: 'House supervisor', phone: wc('74700') },
        { label: 'Security', phone: wc('45400') },
        { label: 'Doctors’ lounge', phones: [{ label: '71562', phone: wc('71562') }, { label: '71563', phone: wc('71563') }] },
      ] },
    ],
  },

  locations: {
    groups: [
      { id: 'wc', title: 'West Campus', items: [
        { name: 'Texas Children’s Hospital West Campus', floor: '18200 Katy Freeway, Houston 77084', note: 'Employee lot south of the Outpatient Building' },
        { name: 'Employee entrance', floor: 'Outpatient Building, west side', note: 'The only one. Main hall, left, main elevators to 3' },
        { name: 'Surgery area', floor: '3rd floor', where: 'Left off the elevators', note: 'Tell the surgery administrator you are the regional resident' },
        { name: 'Regional Anesthesia locker', floor: 'OR locker rooms', where: 'Locker 137 women, 19 men', code: '1359', codeLabel: 'Lock' },
        { name: 'Physician lounge', floor: '3rd floor', note: 'Quiet study space, snacks' },
        { name: 'Nursing lounge', floor: 'OR', code: '2121', codeLabel: 'Door' },
      ] },
    ],
  },

  documents: {
    storageKey: 'tch-regional-pinned-docs',
    groups: [
      { id: 'regional', title: 'Regional', items: [
        { title: 'Regional rotation manual', subtitle: 'West Campus, 2021. Blackboard', url: MANUAL },
        { title: 'NYSORA techniques', url: 'https://www.nysora.com/techniques/' },
      ] },
      ...tchOr.documents.groups,
    ],
  },

  guides: {
    checklist: {
      title: 'Regional Safety Checklist',
      intro: 'After the patient is asleep and the airway is secured. Always before the block.',
      sections: [
        { title: 'Briefing, in holding', blocks: [{ type: 'bullets', items: [
          'Proceduralist present, preferred. The anesthesiologist leads with the circulator, anyone assisting, and the parent',
          'Name and MRN against the armband',
          'Surgical procedure against the H&P or preop note and consent',
          'Regional procedure against its consent',
          'Laterality or level, with dual surgeon and anesthesiologist site marking',
          'Allergies',
          'Discuss and resolve. Everyone agrees out loud, family included. Then to the OR',
        ] }] },
        { title: 'Timeout, in the OR', blocks: [{ type: 'bullets', items: [
          'All activity stops, music off. The anesthesiologist leads with the circulator and says aloud:',
          'Name and procedure again, from the consent',
          'Anticoagulation status',
          'Maximum local anesthetic dose for the weight',
          'Concentration being used',
          'Equipment needed and available',
          'Monitoring applied',
          'Anticipated critical events, concerns, disposition',
          'Any concerns? Everyone agrees out loud',
        ] }] },
        { title: 'Debriefing, before leaving the OR', blocks: [{ type: 'bullets', items: [
          'Anesthesiologist confirms with the proceduralist and circulator:',
          'Local anesthetic given',
          'Local anesthetic available for a supplemental injection',
          'Events or complications to report',
          'Labs or special treatment needed afterwards',
          { text: 'Transfer to the next level of care waits for the brief and anesthesia note', warn: true },
        ] }] },
      ],
      sources: [{ title: 'Regional anesthesia safety checklist v2.0', href: MANUAL, detail: 'Texas Children’s Hospital, 2018. Blackboard, sign-in required' }],
    },

    duties: {
      title: 'Block Day',
      sections: [
        { title: 'The resident’s job', blocks: [{ type: 'steps', items: [
          'Read the day’s schedule for cases that would benefit from a block',
          'Discuss the cases and your plan with the attending',
          'Introduce yourself and consent the patient and family. Get a phone number for follow-up',
          'Set up before the patient arrives: ultrasound, needles, chlorhexidine, gel, gloves, local anesthetic dosed for weight, catheter if planned',
          'Safety checklist once asleep and the airway is secured',
          'Block under direct faculty supervision',
          'Call at 24 hours to confirm motor and sensory function are back. Document it in Epic',
        ] }] },
        { title: 'Good to know', blocks: [{ type: 'bullets', items: [
          'Every pediatric block here is placed in the OR after induction',
          'You do not staff cases or give breaks. Use the gaps to study; the physician lounge on 3 is quiet',
          'No block cases tomorrow: you hear the day before and go to Main Campus to observe the regional fellow',
          'Regional lectures from Main Campus by Zoom, invites to your BCM email',
          'Suggested reading: Miller chapter 46, Morgan and Mikhail on peripheral nerve blocks, NYSORA',
        ] }] },
      ],
      sources: [{ title: 'Regional rotation manual', href: MANUAL, detail: 'Blackboard, sign-in required' }],
    },

    campus: {
      title: 'Getting In',
      sections: [
        { title: 'Arriving', blocks: [{ type: 'steps', items: [
          '06:30 on the first day, 07:00 after. Park in the employee lot south of the Outpatient Building',
          'The only employee entrance is on the west side of the Outpatient Building',
          'Main hall, turn left, main elevators to the 3rd floor. Surgery is on the left',
          'Tell the surgery administrator you are here for regional. They walk you to the OR locker rooms',
          'Scrubs from the ScrubEx machine with your TCH badge. Change, belongings in the Regional Anesthesia locker',
          'Dr Handley or the West Campus GOAT meets you at the OR front desk',
        ] }] },
        { title: 'Attire', blocks: [{ type: 'bullets', items: [
          'TCH scrubs and jackets only. No outside jackets or vests',
          'Hair fully covered; a bouffant over any personal cap, thrown out daily. Beards covered',
        ] }] },
      ],
      sources: [{ title: 'Regional rotation manual', href: MANUAL, detail: 'Blackboard, sign-in required' }],
    },
  },
};
